/* hs-eslint ignored failing-rules */

import { useContext, useRef, useState, useMemo, useCallback } from 'react';
import H4 from 'UIComponents/elements/headings/H4';
import UsageTrackerContext from 'automation-ui-usage-tracking/UsageTrackerContext';
import { trackEvent } from 'automation-ui-usage-tracking/trackingUtils';
import Trackables from 'automation-ui-usage-tracking/Trackables';
import ErrorBoundary from 'automation-ui-core/errors/ErrorBoundary';
import { useUserSettingByKey, useUpdateShepherdDismissedStatus } from 'automation-ui-core/hooks/userSettings/userSettingHooks';
import { OPERATIONS_HUB_ONBOARDING } from 'automation-ui-management/constants/UserSettingKeys';
import FormattedMessage from 'I18n/components/FormattedMessage';
import StyledOperationsHubOnboardingContainer from 'automation-ui-management/components/operations-hub-onboarding/StyledOperationsHubOnboardingContainer';
import StyledOperationsHubOnboardingButton from 'automation-ui-management/components/operations-hub-onboarding/StyledOperationsHubOnboardingButton';
import StyledOperationsHubOnboardingModal from 'automation-ui-management/components/operations-hub-onboarding/StyledOperationsHubOnboardingModal';
import StyledOperationsHubOnboardingHeader from 'automation-ui-management/components/operations-hub-onboarding/StyledOperationsHubOnboardingHeader';
import StyledCloseButton from 'automation-ui-management/components/operations-hub-onboarding/StyledCloseButton';
import OperationsHubOnboardingContent from 'automation-ui-management/components/operations-hub-onboarding/OperationsHubOnboardingContent';
import AuthContext, { hasScope as getHasScope } from 'automation-ui-core/context/AuthContext';
import Scopes from 'automation-ui-management/constants/Scopes';
import { reportError } from 'automation-ui-core/errors/utils/reportError';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ONBOARDING_BUTTON_MARGIN = 16;
const OPERATIONS_HUB_SCOPES = [Scopes.FORMAT_DATA_ACCESS, Scopes.CUSTOM_CODE_ACCESS, Scopes.WEBHOOK_ACCESS, Scopes.SCHEDULED_ENROLLMENT_ACCESS];
function OperationsHubOnboarding() {
  const {
    usageTracker
  } = useContext(UsageTrackerContext);
  const markTourDismissed = useUpdateShepherdDismissedStatus(OPERATIONS_HUB_ONBOARDING);
  const containerRef = useRef(null);
  const [containerOffset, setContainerOffset] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const resizeObserver = useMemo(() => {
    function updateButtonOffset() {
      if (containerRef.current) {
        const offsetParent = containerRef.current.offsetParent;
        const scrollOffset = offsetParent ? offsetParent.scrollLeft : 0;
        const containerRect = containerRef.current.getBoundingClientRect();
        setContainerOffset(Math.max(0, window.innerWidth - containerRect.right - scrollOffset));
      }
    }
    return new ResizeObserver(() => {
      updateButtonOffset();
    });
  }, []);
  const containerFunctionRef = useCallback(container => {
    if (container) {
      containerRef.current = container;
      resizeObserver.observe(container);
    }
  }, [resizeObserver]);
  const handleOpenModal = useCallback(() => {
    setIsOpen(true);
    trackEvent(usageTracker, Trackables.TOGGLED_OPERATIONS_HUB_ONBOARDING, {
      isEnabled: true
    });
  }, [setIsOpen, usageTracker]);
  const handleCloseModal = useCallback(() => {
    markTourDismissed('true').then(() => {
      trackEvent(usageTracker, Trackables.TOGGLED_OPERATIONS_HUB_ONBOARDING, {
        isEnabled: false
      });
    }).catch(error => {
      reportError({
        error
      });
    });
  }, [usageTracker, markTourDismissed]);
  const rightOffset = containerOffset + ONBOARDING_BUTTON_MARGIN;
  return /*#__PURE__*/_jsxs(StyledOperationsHubOnboardingContainer, {
    ref: containerFunctionRef,
    children: [/*#__PURE__*/_jsx(StyledOperationsHubOnboardingButton, {
      isOpen: isOpen,
      "aria-hidden": isOpen,
      onClick: handleOpenModal,
      style: {
        right: `${rightOffset}px`
      },
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "automation-ui-management.operations-hub-onboarding.buttonLabel"
      })
    }), isOpen && /*#__PURE__*/_jsxs(StyledOperationsHubOnboardingModal, {
      isOpen: true,
      style: {
        right: `${rightOffset}px`
      },
      children: [/*#__PURE__*/_jsxs(StyledOperationsHubOnboardingHeader, {
        children: [/*#__PURE__*/_jsx(H4, {
          "aria-level": 2,
          className: "m-all-0",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "automation-ui-management.operations-hub-onboarding.buttonLabel"
          })
        }), /*#__PURE__*/_jsx(StyledCloseButton, {
          onClick: handleCloseModal
        })]
      }), /*#__PURE__*/_jsx(OperationsHubOnboardingContent, {})]
    })]
  });
}
function RestrictedOperationsHubOnboarding() {
  const {
    user
  } = useContext(AuthContext);
  const hasScope = getHasScope(user.scopes);
  const isOnboardingDismissed = useUserSettingByKey(OPERATIONS_HUB_ONBOARDING);
  const hasOperationsHubAccess = OPERATIONS_HUB_SCOPES.every(scope => hasScope(scope));
  if (!hasOperationsHubAccess || isOnboardingDismissed === undefined || isOnboardingDismissed) {
    return null;
  }
  return /*#__PURE__*/_jsx(ErrorBoundary, {
    boundaryName: "OperationsHubOnboarding",
    renderError: () => /*#__PURE__*/_jsx("span", {}),
    children: /*#__PURE__*/_jsx(OperationsHubOnboarding, {})
  });
}
export default RestrictedOperationsHubOnboarding;